import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import { MaskedLines } from "@/lib/motion";

const HERO_IMG = "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHx3YXJtJTIwY29tbXVuaXR5JTIwc3VwcG9ydCUyMGdyb3VwfGVufDB8fHx8MTc4MzczOTAxNHww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { t } = useLang();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section ref={ref} className="relative pt-[72px] overflow-hidden bg-bone" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center min-h-[calc(100vh-72px)] py-14 lg:py-0">
          {/* Left: text */}
          <div className="lg:col-span-6 xl:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-pine-300/70 bg-bone-100 px-4 py-1.5 mb-7"
            >
              <MapPin className="w-3.5 h-3.5 text-terracotta" />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-pine-800">{t.hero.badge}</span>
            </motion.div>

            <h1 className="font-serif font-light text-pine-900 text-5xl md:text-6xl xl:text-7xl leading-[1.02] tracking-tight">
              <MaskedLines lines={t.hero.title} />
            </h1>

            <motion.p
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-8 max-w-xl text-lg leading-relaxed text-pine-700"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={() => scrollTo("history")}
                className="group inline-flex items-center gap-2 rounded-full bg-pine-900 hover:bg-pine-800 text-bone px-7 py-3.5 text-[15px] font-semibold transition-colors duration-200"
                data-testid="hero-cta-primary"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => scrollTo("services")}
                className="inline-flex items-center gap-2 rounded-full border-2 border-pine-900 text-pine-900 hover:bg-pine-900 hover:text-bone px-7 py-3.5 text-[15px] font-semibold transition-colors duration-200"
                data-testid="hero-cta-secondary"
              >
                {t.hero.ctaSecondary}
              </button>
            </motion.div>
          </div>

          {/* Right: image */}
          <div className="lg:col-span-6 xl:col-span-6">
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-[2rem] overflow-hidden aspect-[4/5] max-h-[620px] w-full shadow-[0_30px_80px_rgba(26,46,37,0.18)] grain"
            >
              <motion.img
                src={HERO_IMG}
                alt={t.hero.overline}
                style={{ y, scale }}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-pine-900/45 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="rounded-2xl bg-bone/90 backdrop-blur-md px-5 py-4 border border-bone-200">
                  <p className="font-serif italic text-pine-900 text-lg leading-snug">{t.hero.overline}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
